import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SMARTBNB_API_V1 } from '../../interfaces/injection-tokens.interface';
import {
  GetPropertiesRequest,
  GetPropertiesResponse,
  Property,
  PropertyIncludes,
} from '../../interfaces/property.interface';

@Injectable({
  providedIn: 'root',
})
export class PropertiesService {
  private readonly http = inject(HttpClient);
  private readonly smartbnbApiV1 = inject(SMARTBNB_API_V1);

  private get propertiesUrl(): string {
    return `${this.smartbnbApiV1}/properties`;
  }

  private get propertyUrl(): string {
    return `${this.smartbnbApiV1}/properties/`;
  }

  getProperties(request: GetPropertiesRequest = {}): Observable<GetPropertiesResponse> {
    let params = new HttpParams();

    if (request.paginate === false) {
      params = params.append('pagination', 'false');
    } else if (request.limit) {
      params = params.append('limit', request.limit.toString());
    }

    if (request.offset) {
      params = params.append('offset', request.offset.toString());
    }

    if (request.search) {
      params = params.append('query', request.search);
    }

    if (request.transformer) {
      params = params.append('transformer', request.transformer);
    }

    if (request.idList?.length) {
      params = params.append('ids', request.idList.join());
    }

    if (request.includes?.length) {
      params = params.append('include', request.includes.join());
    }

    const filters = request.filterCriteria || [];

    return this.http.post<GetPropertiesResponse>(this.propertiesUrl, { filters }, { params }).pipe(
      map((res) => res),
      catchError(this.handleError)
    );
  }

  getProperty(id: string | number, includes: PropertyIncludes[] = []): Observable<Property> {
    const url = `${this.propertyUrl}${id}`;
    let params = new HttpParams();

    if (includes?.length) {
      params = params.append('include', includes.join(','));
    }

    return this.http.get<{ data: Property }>(url, { params }).pipe(
      map((res) => {
        const property = res.data;
        if (!property) {
          return property;
        }

        // Check if the property ID from the response differs from the requested ID
        // This could indicate a redirect scenario (e.g., merged properties)
        const returnedId = property.id;
        const requestedId = id.toString();

        if (returnedId && returnedId.toString() !== requestedId) {
          // Store the redirect information for components to handle
          property._wasRedirected = true;
          property._originalRequestedId = requestedId;
        }

        return property;
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: unknown): Observable<never> {
    console.error('PropertiesService error:', error);
    throw error;
  }
}
